<script setup>
import { useSlots } from 'vue';
import CustomBrandPolicyWrapper from 'dashboard/components/CustomBrandPolicyWrapper.vue';
import { getHelpUrlForFeature } from '../../../../helper/featureHelper';
import BackButton from '../../../../components/widgets/BackButton.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Input from 'dashboard/components-next/input/Input.vue';

const props = defineProps({
  title: {
    type: String,
    required: true,
  },
  description: {
    type: String,
    default: '',
  },
  linkText: {
    type: String,
    default: '',
  },
  featureName: {
    type: String,
    default: '',
  },
  backButtonLabel: {
    type: String,
    default: '',
  },
  searchPlaceholder: {
    type: String,
    default: '',
  },
});

const slots = useSlots();

const searchQuery = defineModel('searchQuery', { type: String, default: '' });

const helpURL = getHelpUrlForFeature(props.featureName);
</script>

<template>
  <header
    class="flex min-w-0 flex-col gap-4 rounded-lg border border-n-weak bg-n-surface-1 p-4 sm:p-5"
  >
    <div class="flex min-w-0 flex-col items-start gap-2">
      <BackButton
        v-if="backButtonLabel"
        compact
        :button-label="backButtonLabel"
        class="my-1"
      />
      <div
        v-if="title"
        class="flex items-center justify-between w-full gap-4 min-h-8"
      >
        <h1 class="mb-0 text-xl font-semibold leading-7 text-n-slate-12">
          {{ title }}
        </h1>
      </div>
      <div
        v-if="description || $slots.description || linkText || helpURL"
        class="flex flex-col w-full gap-1.5 text-n-slate-11"
      >
        <p
          v-if="description || $slots.description"
          class="mb-0 max-w-3xl text-sm leading-6"
        >
          <slot name="description">{{ description }}</slot>
        </p>
        <CustomBrandPolicyWrapper :show-on-custom-branded-instance="false">
          <a
            v-if="helpURL && linkText"
            :href="helpURL"
            target="_blank"
            rel="noopener noreferrer"
            class="items-center hidden gap-1 text-sm font-medium sm:inline-flex w-fit text-n-blue-11 hover:underline mb-2"
          >
            {{ linkText }}
            <Icon
              icon="i-lucide-chevron-right"
              class="flex-shrink-0 text-n-blue-11 size-4"
            />
          </a>
        </CustomBrandPolicyWrapper>
      </div>
    </div>
    <div
      v-if="searchPlaceholder || slots.actions || slots.tabs"
      class="flex min-w-0 flex-wrap items-center justify-between gap-3 border-t border-n-weak pt-4"
    >
      <div
        v-if="slots.tabs || searchPlaceholder"
        class="flex w-full min-w-0 flex-wrap items-center gap-3 sm:w-auto"
      >
        <slot name="tabs" />
        <Input
          v-if="searchPlaceholder"
          v-model="searchQuery"
          :placeholder="searchPlaceholder"
          :aria-label="searchPlaceholder"
          class="group w-full min-w-0 sm:w-64 [&>input]:ltr:!pl-9 [&>input]:rtl:!pr-9"
          type="search"
        >
          <template #prefix>
            <Icon
              icon="i-lucide-search"
              class="absolute top-1/2 -translate-y-1/2 text-n-slate-11 group-focus-within:text-n-brand size-3.5 ltr:left-2.5 rtl:right-2.5"
            />
          </template>
        </Input>
      </div>
      <div class="flex min-w-0 flex-wrap items-center gap-3">
        <slot name="count" />
        <div v-if="slots.count" class="w-px h-3 bg-n-weak shrink-0" />
        <slot name="actions" />
      </div>
    </div>
  </header>
</template>
